import { Stack } from "@mui/material";

import {
  BookedPriceRevealLocked,
  BookedPriceRevealResult,
} from "components/Carrier/components/BookedPriceReveal";
import { useSubscriptionStatus } from "hooks/useSubscriptionStatus";

interface Props {
  shipment: CarrierCondensedShipment;
  dividerComponent: JSX.Element;
}

/**
 * Booked price reveal decision tree:
 *
 * 1. Essentials / Pay-Per-Booking → <BookedPriceRevealLocked />
 *    Shows upgrade CTA regardless of whether the carrier quoted the load.
 *
 * 2. Bronze, Silver, Gold, Platinum (paying carriers):
 *    a. Carrier booked the shipment → null (no section rendered)
 *       Remains true as the shipment transitions through
 *       BOOKED → PICKED_UP → DELIVERED and BOOKING_CANCELLED.
 *
 *    b. Otherwise → <BookedPriceRevealResult /> fetches booked-price from
 *       backend, which owns every remaining decision:
 *         i.  Backend reports the reveal is unavailable (the booking was
 *             cancelled, the carrier never quoted, the shipment type cannot be
 *             graded) → <BookedPriceRevealUnavailable /> with that reason
 *
 *         ii. Backend returns a rating → <BookedPriceRevealTier /> with tier:
 *             - HIGHLY_COMPETITIVE : within 5%
 *             - COMPETITIVE        : within 6–12%
 *             - IN_THE_BALLPARK    : within 13–25%
 *             - NOT_COMPETITIVE    : more than 25%
 *
 *             and, when the shipper company discloses it, the exact booked
 *             price above the band. That choice is the backend's too — see
 *             docs/engineering/design/shipper_company_price_reveal_control_design.md.
 */
export function BookedPriceRevealSection(props: Props) {
  const { shipment, dividerComponent } = props;
  const { isPayingCarrier } = useSubscriptionStatus();

  if (
    shipment?.info?.booked_at == null ||
    shipment.booked_by_carrier ||
    isPayingCarrier == null
  ) {
    // scenarios where no component is rendered.
    return null;
  }

  const revealPriceComponent = isPayingCarrier ? (
    <BookedPriceRevealResult shipmentId={shipment.info.id} />
  ) : (
    <BookedPriceRevealLocked />
  );

  return (
    <Stack direction="column" spacing={1} mb="1rem">
      {dividerComponent}
      <Stack>{revealPriceComponent}</Stack>
    </Stack>
  );
}
